import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Form, InputGroup, Spinner,
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { shopeeShoppingAPI, supplierMaterialAPI } from '../../services/purchaseOrder';
import { branchAPI } from '../../services/branch';
import { parseShopeeLink } from '../../utils/shopeeLink';

const fmtQty = (n) => Number(n ?? 0).toLocaleString('vi-VN', { maximumFractionDigits: 3 });
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('vi-VN') : '—');

const pkgLabel = (pkg, unit) => pkg?.unit_label || pkg?.name || unit || '';

/**
 * "Cần mua qua Shopee" (#233654) — DANH SÁCH ĐI CHỢ, KHÔNG PHẢI ĐƠN ĐẶT HÀNG.
 *
 * Sếp chốt hướng BÁN TỰ ĐỘNG (nút `sp_ban_tudong`): máy dò ra cần mua gì, bao nhiêu, và mở thẳng
 * đúng trang sản phẩm; NGƯỜI bấm mua và trả tiền. Trang này KHÔNG gọi API Shopee, KHÔNG đặt hàng
 * hộ, KHÔNG giả lập bấm trình duyệt — Shopee không có API cho người MUA và làm vậy là vi phạm
 * điều khoản + tiền thật rời công ty.
 *
 * Thiết kế cho ĐIỆN THOẠI trước (sếp dùng điện thoại): mỗi NVL là 1 thẻ, nút mở Shopee to, bấm
 * được bằng ngón tay ở khổ 390×844.
 */
export default function ShopeeShoppingListPage() {
  const { branches, selectedBranch } = useBranch();
  // #002620 — nhận ?branch=<id> để đi từ màn "NCC đang cần đặt hàng" sang không mất chi nhánh
  // đang xem. Không có tham số thì giữ nguyên hành vi cũ (tự chọn chi nhánh đang đứng).
  const [searchParams] = useSearchParams();

  const [branchId, setBranchId] = useState(searchParams.get('branch') || '');
  /**
   * Nguồn chi nhánh: đọc thẳng `/branches/active/simple` (đúng tiền lệ #125114) chứ KHÔNG dùng
   * `useBranch().branches` — danh sách trong context bị bó theo chi nhánh người dùng đang đứng,
   * nên chỉ hiện 1 mục và không lọc được Q10 / Bình Thạnh / Thủ Đức như đề bài yêu cầu.
   * Mở chi nhánh thứ 4 là tự có mặt, không hardcode.
   */
  const [allBranches, setAllBranches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  // SL người dùng sửa tay: { [supplier_material_id | material_id]: chuỗi đang gõ }
  const [qtyDraft, setQtyDraft] = useState({});
  // Link đang gõ ở nhóm "Chưa gắn link": { [supplier_material_id]: chuỗi }
  const [linkDraft, setLinkDraft] = useState({});

  useEffect(() => {
    let alive = true;
    branchAPI.getActiveSimple()
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res) ? res : res?.data || [];
        setAllBranches(list);
      })
      .catch(() => { /* rơi về danh sách trong context ở dưới */ });
    return () => { alive = false; };
  }, []);

  const branchOptions = useMemo(
    () => (allBranches.length ? allBranches : (branches || [])),
    [allBranches, branches],
  );

  useEffect(() => {
    if (branchId || !branchOptions.length) return;
    const preferred = branchOptions.find((b) => b.id === selectedBranch?.id);
    setBranchId((preferred || branchOptions[0]).id);
  }, [branchOptions, selectedBranch, branchId]);

  const load = useCallback(async () => {
    if (!branchId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await shopeeShoppingAPI.getList(branchId);
      setData(res);
      setQtyDraft({});
      setLinkDraft({});
    } catch (err) {
      setError(err?.message || 'Không tải được danh sách cần mua');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const matches = useCallback(
    (name) => !search || (name || '').toLowerCase().includes(search.toLowerCase()),
    [search],
  );

  const readyItems = useMemo(
    () => (data?.items || []).filter((i) => matches(i.material_name)),
    [data, matches],
  );
  const missingItems = useMemo(
    () => (data?.missing_link || []).filter((i) => matches(i.material_name)),
    [data, matches],
  );

  /** SL đang hiển thị cho 1 lựa chọn mua: ưu tiên số người dùng vừa sửa, sau đó là số gợi ý. */
  const qtyOf = (key, suggested) => {
    const d = qtyDraft[key];
    if (d !== undefined) return d;
    return suggested === null || suggested === undefined ? '' : String(suggested);
  };

  const handleMark = async (item, option) => {
    const key = option.supplier_material_id;
    const raw = qtyOf(key, option.suggested_packages);
    const qty = Number(raw);
    if (!raw || !Number.isFinite(qty) || qty <= 0) {
      toast.error('Nhập số lượng đã mua (lớn hơn 0) trước khi tick');
      return;
    }
    try {
      setBusyKey(key);
      await shopeeShoppingAPI.markPurchased({
        branch_id: branchId,
        material_id: item.material_id,
        supplier_material_id: option.supplier_material_id,
        quantity: qty,
        package_label: pkgLabel(option.package, item.unit),
        suggested_quantity: option.suggested_packages ?? undefined,
      });
      toast.success(`Đã ghi "đã mua": ${item.material_name}`);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Không ghi được vết đã mua');
    } finally {
      setBusyKey(null);
    }
  };

  const handleUnmark = async (bought, key) => {
    try {
      setBusyKey(key);
      await shopeeShoppingAPI.unmarkPurchased(bought.id);
      toast.success('Đã bỏ tick "đã mua"');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Không bỏ được tick');
    } finally {
      setBusyKey(null);
    }
  };

  /** Gắn link ngay tại chỗ cho NVL đang nằm ở nhóm "Chưa gắn link". */
  const handleAttachLink = async (supplierMaterialId) => {
    const raw = (linkDraft[supplierMaterialId] || '').trim();
    const info = parseShopeeLink(raw);
    if (info.kind !== 'PRODUCT' && info.kind !== 'SHORT') {
      toast.error(info.message || 'Link Shopee không hợp lệ');
      return;
    }
    try {
      setBusyKey(supplierMaterialId);
      await supplierMaterialAPI.update(supplierMaterialId, { shopee_url: raw });
      toast.success('Đã gắn link Shopee');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Không gắn được link');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <PageLayout>
      <div className="container-fluid px-2 px-md-3 py-3">
        <h4 className="mb-1">Cần mua qua Shopee</h4>
        <p className="text-muted small mb-3">
          Danh sách <strong>đi chợ</strong> — không phải đơn đặt hàng. Máy dò ra cần mua gì và mở
          thẳng sản phẩm; <strong>bạn tự bấm mua và trả tiền trên Shopee</strong>.
        </p>

        {/* Bộ lọc: chi nhánh + tìm NVL */}
        <Card className="mb-3">
          <Card.Body className="p-2 p-md-3">
            <div className="row g-2">
              <div className="col-12 col-md-5">
                <Form.Label className="small mb-1">Chi nhánh</Form.Label>
                <Form.Select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={loading}
                >
                  {!branchOptions.length && <option value="">(chưa có chi nhánh)</option>}
                  {branchOptions.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                  Mỗi chi nhánh cần mua khác nhau — đổi chi nhánh là danh sách đổi theo.
                </Form.Text>
              </div>
              <div className="col-12 col-md-5">
                <Form.Label className="small mb-1">Tìm NVL</Form.Label>
                <Form.Control
                  placeholder="Gõ tên NVL..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-2 d-flex align-items-end">
                <Button variant="outline-secondary" className="w-100" onClick={load} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Tải lại'}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Nói rõ số gợi ý ở đâu ra — KHÔNG để người dùng tưởng là số đã kiểm chứng */}
        {data?.quantity_source && (
          <Alert variant="warning" className="py-2 px-3 small">
            <strong>Số lượng gợi ý ở đâu ra?</strong> {data.quantity_source}
          </Alert>
        )}

        {error && <Alert variant="danger" className="py-2 px-3 small">{error}</Alert>}

        {loading && !data && (
          <div className="text-center py-5"><Spinner /></div>
        )}

        {data && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge bg="primary">Tổng cần mua: {data.summary.total}</Badge>
            <Badge bg="success">Đã có link: {data.summary.ready}</Badge>
            <Badge bg="danger">Chưa gắn link: {data.summary.missing_link}</Badge>
            <Badge bg="secondary">Tính "đã mua" trong {data.lookback_days} ngày gần nhất</Badge>
          </div>
        )}

        {data && data.summary.total === 0 && (
          <Alert variant="success" className="py-2 px-3">
            Không có NVL nào dưới ngưỡng tối thiểu ở chi nhánh này — chưa cần đi chợ.
          </Alert>
        )}

        {/* ── Nhóm 1: đã có link, mua được ngay ─────────────────────────────── */}
        {readyItems.map((item) => (
          <Card key={item.material_id} className="mb-2">
            <Card.Body className="p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <div className="fw-bold">{item.material_name}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Tồn: {fmtQty(item.current_stock)} {item.unit}
                    {' · '}Ngưỡng tối thiểu: {fmtQty(item.min_stock_threshold)} {item.unit}
                    {' · '}
                    {item.effective_par_level === null
                      ? <span className="text-danger">chưa đặt định mức</span>
                      : <>Định mức: {fmtQty(item.effective_par_level)} {item.unit}</>}
                  </div>
                </div>
                <Badge bg="danger">Cần mua</Badge>
              </div>

              {item.options.map((opt) => {
                const key = opt.supplier_material_id;
                const bought = opt.bought;
                return (
                  <div key={key} className="border rounded p-2 mt-2 bg-light">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <div className="small fw-medium text-truncate">
                        {opt.supplier_name}
                        <span className="text-muted"> · {pkgLabel(opt.package, item.unit)}</span>
                      </div>
                      {bought && <Badge bg="success">Đã mua</Badge>}
                    </div>

                    <div className="row g-2 align-items-end">
                      <div className="col-6 col-md-3">
                        <Form.Label className="small mb-1">
                          SL ({pkgLabel(opt.package, item.unit)})
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="any"
                          inputMode="decimal"
                          placeholder={opt.suggested_packages === null ? 'tự nhập' : ''}
                          value={qtyOf(key, opt.suggested_packages)}
                          onChange={(e) => setQtyDraft((p) => ({ ...p, [key]: e.target.value }))}
                          disabled={!!bought || busyKey === key}
                        />
                      </div>

                      <div className="col-6 col-md-4">
                        <Button
                          variant="warning"
                          className="w-100 fw-bold"
                          href={opt.shopee_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          🛒 Mở Shopee
                        </Button>
                      </div>

                      <div className="col-12 col-md-5">
                        {bought ? (
                          <Button
                            variant="outline-secondary"
                            className="w-100"
                            onClick={() => handleUnmark(bought, key)}
                            disabled={busyKey === key}
                          >
                            {busyKey === key ? <Spinner size="sm" /> : 'Bỏ tick đã mua'}
                          </Button>
                        ) : (
                          <Button
                            variant="success"
                            className="w-100"
                            onClick={() => handleMark(item, opt)}
                            disabled={busyKey === key}
                          >
                            {busyKey === key ? <Spinner size="sm" /> : '✓ Đã mua'}
                          </Button>
                        )}
                      </div>
                    </div>

                    {opt.suggested_packages === null && !bought && (
                      <div className="text-danger mt-1" style={{ fontSize: 11 }}>
                        NVL này chưa đặt định mức nên máy không suy ra được số cần mua — hãy tự nhập.
                      </div>
                    )}

                    {bought && (
                      <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                        {fmtQty(bought.quantity)} {bought.package_label || ''}
                        {' · '}{bought.marked_by_name || 'ai đó'}
                        {' · '}{fmtDateTime(bought.created_at)}
                        {bought.suggested_quantity !== null
                          && bought.suggested_quantity !== undefined
                          && Number(bought.suggested_quantity) !== Number(bought.quantity)
                          && ` · (gợi ý lúc đó: ${fmtQty(bought.suggested_quantity)})`}
                      </div>
                    )}
                  </div>
                );
              })}
            </Card.Body>
          </Card>
        ))}

        {/* ── Nhóm 2: chưa gắn link — KHÔNG ẨN ĐI ───────────────────────────── */}
        {missingItems.length > 0 && (
          <>
            <h6 className="mt-4 mb-2 text-danger">
              Chưa gắn link Shopee ({missingItems.length}) — vẫn cần mua, chưa mở thẳng được
            </h6>
            {missingItems.map((item) => (
              <Card key={item.material_id} className="mb-2 border-danger">
                <Card.Body className="p-2 p-md-3">
                  <div className="fw-bold">{item.material_name}</div>
                  <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                    Tồn: {fmtQty(item.current_stock)} {item.unit}
                    {' · '}Ngưỡng tối thiểu: {fmtQty(item.min_stock_threshold)} {item.unit}
                  </div>

                  {item.suppliers.length === 0 ? (
                    <Alert variant="secondary" className="py-2 px-2 mb-0 small">
                      NVL này chưa gắn với nhà cung cấp nào. Vào <strong>Kho → Nhà cung cấp →
                      Quản lý NVL</strong> để thêm NVL vào 1 NCC rồi dán link.
                    </Alert>
                  ) : (
                    item.suppliers.map((sup) => {
                      const key = sup.supplier_material_id;
                      const draft = linkDraft[key] || '';
                      const info = draft.trim() ? parseShopeeLink(draft) : null;
                      const bad = info && info.kind !== 'PRODUCT' && info.kind !== 'SHORT';
                      return (
                        <div key={key} className="mb-2">
                          <div className="small fw-medium mb-1">{sup.supplier_name}</div>
                          <InputGroup size="sm">
                            <Form.Control
                              type="url"
                              inputMode="url"
                              placeholder="Dán link sản phẩm Shopee..."
                              value={draft}
                              isInvalid={!!bad}
                              onChange={(e) => setLinkDraft((p) => ({ ...p, [key]: e.target.value }))}
                              disabled={busyKey === key}
                            />
                            <Button
                              variant="primary"
                              onClick={() => handleAttachLink(key)}
                              disabled={busyKey === key || !draft.trim() || !!bad}
                            >
                              {busyKey === key ? <Spinner size="sm" /> : 'Gắn link'}
                            </Button>
                          </InputGroup>
                          {info?.message && (
                            <div className={bad ? 'text-danger' : 'text-warning'} style={{ fontSize: 11 }}>
                              {info.message}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </Card.Body>
              </Card>
            ))}
          </>
        )}
      </div>
    </PageLayout>
  );
}
